// 'use client';

// React Imports
import { ReactNode, createContext, useMemo, useState } from 'react';

import type {} from '@mui/lab/themeAugmentation'; //! Do not remove this import otherwise you will get type errors while making a production build
import CssBaseline from '@mui/material/CssBaseline';
import {
  Experimental_CssVarsProvider as CssVarsProvider,
  darken,
  experimental_extendTheme as extendTheme,
  lighten,
} from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation'; //! Do not remove this import otherwise you will get type errors while making a production build
// MUI Imports
import { deepmerge } from '@mui/utils';

// Third-party Imports
import { useMedia } from 'react-use';
import stylisRTLPlugin from 'stylis-plugin-rtl';

// Type Imports
import type { ChildrenType, Direction, SystemMode } from '../../@core/types';

// Component Imports

// Config Imports
import themeConfig from '../../configs/themeConfig';

// Hook Imports
import { useSettings } from '../../@core/hooks/useSettings';

// Core Theme Imports
import defaultCoreTheme from '../../@core/theme';
import ModeChanger from './ModeChanger';

type Props = ChildrenType & {
  direction: Direction;
  systemMode: SystemMode;
};
const CacheContext = createContext<any>(null);
const CacheProvider = ({
  options,
  children,
}: {
  options: any;
  children: ReactNode;
}) => {
  const [cache, setCache] = useState<any>({}); // Initialize your cache

  // Implement any other cache logic here as needed

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <CacheContext.Provider value={{ cache, setCache, prepend: options.prepend, key: options.key }}>
      {children}
    </CacheContext.Provider>
  );
};

const ThemeProvider = (props: Props) => {
  // Props
  const { children, direction, systemMode } = props;

  // Vars
  const isServer = typeof window === 'undefined';
  let currentMode: SystemMode;

  // Hooks
  const { settings } = useSettings();
  const isDark = useMedia('(prefers-color-scheme: dark)', systemMode === 'dark');

  if (isServer) {
    currentMode = systemMode;
  } else if (settings.mode === 'system') {
    currentMode = isDark ? 'dark' : 'light';
  } else {
    currentMode = settings.mode as SystemMode;
  }

  // Merge the primary color scheme override with the core theme
  const theme = useMemo(() => {
    const newColorScheme = {
      colorSchemes: {
        light: {
          palette: {
            primary: {
              main: settings.primaryColor,
              light: lighten(settings.primaryColor as string, 0.2),
              dark: darken(settings.primaryColor as string, 0.1),
            },
            /* background: {
              default: '#FFF',
            }, */
          },
        },
        dark: {
          palette: {
            primary: {
              main: settings.primaryColor,
              light: lighten(settings.primaryColor as string, 0.2),
              dark: darken(settings.primaryColor as string, 0.1),
            },
          },
        },
      },
    };

    const coreTheme = deepmerge(defaultCoreTheme(settings, currentMode, direction), newColorScheme);

    return extendTheme(coreTheme);
  }, [settings.primaryColor, settings.skin, currentMode]);

  return (
    <CacheProvider
      options={{
        prepend: true,
        ...(direction === 'rtl' && {
          key: 'rtl',
          stylisPlugins: [stylisRTLPlugin],
        }),
      }}
    >
      <CssVarsProvider
        theme={theme}
        defaultMode={systemMode}
        modeStorageKey={`${themeConfig.templateName.toLowerCase().split(' ').join('-')}-mui-template-mode`}
      >
        <>
          <ModeChanger systemMode={systemMode} />
          <CssBaseline />
          {children}
        </>
      </CssVarsProvider>
    </CacheProvider>
  );
};

export default ThemeProvider;
